/**
 * Okul ayarları — saat dilimi, eksi bakiye toleransı, kart yenileme ücreti,
 * fotoğrafsız kart offline tavanı (KK-537), gün sonu mail saati (KK-604) ve
 * banka hesabı (KK-1207).
 *
 * Bu ekrandaki her alan PARA RİSKİNİ doğrudan değiştirir; değişiklikler
 * önceki/sonraki değerleriyle denetim kaydına yazılır.
 *
 * KK-1207 IBAN: sistemdeki EN TEHLİKELİ yapılandırma değişikliğidir. Yanlış
 * (veya kötü niyetli) bir IBAN tüm velilerin parasını başka hesaba yönlendirir;
 * bu yüzden kaydetmeden önce açık onay istenir.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  OkulLogosu,
  Rozet,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { ROL_ADI, Rol, TUM_ROLLER, VARSAYILAN, para } from "@kantin/shared";
import { useYetkili } from "../../veri/oturum.js";
import { apiOku, apiYaz, tokenOku } from "./api.js";
import { medyaYukle } from "../../veri/medya.js";
import { useOkul } from "./kancalar.js";
import type { Okul } from "./kancalar.js";
import {
  IslemDurumu,
  OnayKutusu,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
  paraGecerliMi,
  paraNormalize,
} from "./ortak.js";

interface BankaHesabi {
  id: string;
  banka_adi: string;
  hesap_adi: string;
  iban: string;
  aciklama_sablonu: string;
  aktif: boolean;
}

/** KK-1207: TR + 24 rakam. Boşluklar temizlenir; kullanıcıya "yanlış" demek yerine normalize edilir. */
function ibanNormalize(ham: string): string {
  return ham.replace(/\s+/g, "").toLocaleUpperCase("tr");
}

function ibanGecerliMi(ham: string): boolean {
  return /^TR\d{24}$/.test(ibanNormalize(ham));
}

/** Görsel biçim: TR12 3456 7890 ... */
function ibanGoster(iban: string): string {
  return ibanNormalize(iban)
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

function saatDilimiGecerliMi(deger: string): boolean {
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone: deger });
    return true;
  } catch {
    return false;
  }
}

export function OkulAyarlari() {
  const okul = useOkul();
  // §5.1: okul ayarı ve IBAN kurum kararıdır; kantin müdürü okur, yazamaz.
  const yazabilir = useYetkili(Rol.OKUL_YONETIMI, Rol.OKUL_SISTEM_YONETICISI);

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Okul ayarları"
        aciklama="Saat dilimi, bakiye politikaları, kart ücreti, gün sonu maili ve okulun banka hesabı."
      />

      {!yazabilir && <SaltOkunurUyarisi />}

      <SorguDurumu
        yukleniyor={okul.isPending}
        hata={okul.error}
        bos={!okul.data}
        bosBaslik="Okul kaydı bulunamadı"
        yenile={() => void okul.refetch()}
      >
        {okul.data && <>
          <LogoFormu okul={okul.data} yazabilir={yazabilir} />
          <AyarFormu okul={okul.data} yazabilir={yazabilir} />
        </>}
      </SorguDurumu>

      <BankaHesaplari yazabilir={yazabilir} />
    </Sayfa>
  );
}

/** Logo, diğer okul ayarlarından bağımsız kaydedilir. */
function LogoFormu({ okul, yazabilir }: { okul: Okul; yazabilir: boolean }) {
  const istemci = useQueryClient();
  const kaydet = useMutation({
    mutationFn: async (dosya: File | null) => {
      if (dosya && (!["image/jpeg", "image/png", "image/webp"].includes(dosya.type) || dosya.size > 2 * 1024 * 1024)) {
        throw new Error("En fazla 2 MB JPEG, PNG veya WEBP dosyası seçin.");
      }
      const medya = dosya ? await medyaYukle(dosya, "okul_logo") : null;
      return apiYaz<Okul>("PATCH", "/okul", { logo_medya_id: medya?.medya_id ?? null });
    },
    onSuccess: (yeni) => {
      istemci.setQueryData(["okul"], yeni);
      window.dispatchEvent(new Event("kantin:okul-logosu-degisti"));
    },
  });
  return (
    <Kart baslik="Okul logosu">
      <div className="flex flex-wrap items-center gap-4">
        <OkulLogosu ad={okul.ad} token={tokenOku()} boyut={80}
          medyaAdresi={okul.logo_medya_id ? `/api/v1/medya/${okul.logo_medya_id}` : null} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-murekkep-2">Kasa, veli, yönetim ve platform panellerinde gösterilir. JPEG, PNG veya WEBP; en fazla 2 MB.</p>
          {yazabilir && <div className="flex flex-wrap items-center gap-3">
            <label className="flex min-w-0 flex-col gap-1 text-sm">
              {okul.logo_medya_id ? "Logoyu değiştir" : "Logo yükle"}
              <input type="file" accept="image/jpeg,image/png,image/webp"
                className="max-w-full" disabled={kaydet.isPending}
                onChange={(e) => {
                  const dosya = e.target.files?.[0];
                  e.target.value = "";
                  if (dosya) kaydet.mutate(dosya);
                }} />
            </label>
            {okul.logo_medya_id && <Buton tur="ikincil" disabled={kaydet.isPending}
              onClick={() => kaydet.mutate(null)}>Logoyu kaldır</Buton>}
          </div>}
          <div role="status" className="mt-2 text-sm">
            {kaydet.isPending ? "Logo kaydediliyor…" : kaydet.isSuccess ? "Logo güncellendi." : null}
          </div>
          {kaydet.error && <Uyari tur="hata">{kaydet.error.message}</Uyari>}
        </div>
      </div>
    </Kart>
  );
}

function SaltOkunurUyarisi() {
  return (
    <Uyari tur="bilgi" baslik="Bu ayarlar size salt okunur açıktır">
      Okul politikaları ve banka hesabı yalnızca{" "}
      <strong>{ROL_ADI[Rol.OKUL_YONETIMI]}</strong> rolüyle değiştirilebilir;
      kantin yönetimi kendi yetkisini genişletemesin diye ayrılmıştır.
      Değişiklik için okul yönetimine başvurun.
    </Uyari>
  );
}

// ---------------------------------------------------------------------------

function AyarFormu({ okul, yazabilir }: { okul: Okul; yazabilir: boolean }) {
  const istemci = useQueryClient();
  const [saatDilimi, setSaatDilimi] = useState(okul.saat_dilimi);
  const [eksiTolerans, setEksiTolerans] = useState(okul.eksi_tolerans);
  const [eksiBakiyeAcik, setEksiBakiyeAcik] = useState(
    para(okul.eksi_tolerans).gt(0),
  );
  const [kartUcreti, setKartUcreti] = useState(okul.kart_yenileme_ucreti);
  const [fotosuzAcik, setFotosuzAcik] = useState(
    okul.fotosuz_offline_tavan !== null,
  );
  const [fotosuzTavan, setFotosuzTavan] = useState(
    okul.fotosuz_offline_tavan ?? "",
  );
  const [mailAcik, setMailAcik] = useState(okul.gun_sonu_mail_saati !== null);
  const [mailSaati, setMailSaati] = useState(
    okul.gun_sonu_mail_saati ?? "16:30",
  );
  const [kullanilabilirRoller, setKullanilabilirRoller] = useState(
    okul.kullanilabilir_roller,
  );
  const [gonderildi, setGonderildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<Okul>("PATCH", "/okul", {
        saat_dilimi: saatDilimi.trim(),
        eksi_tolerans: eksiBakiyeAcik ? paraNormalize(eksiTolerans) : "0.00",
        kart_yenileme_ucreti: paraNormalize(kartUcreti),
        fotosuz_offline_tavan:
          fotosuzAcik && fotosuzTavan.trim()
            ? paraNormalize(fotosuzTavan)
            : null,
        gun_sonu_mail_saati: mailAcik ? mailSaati : null,
        kullanilabilir_roller: kullanilabilirRoller,
      }),
    onSuccess: () => {
      setGonderildi(false);
      void istemci.invalidateQueries({ queryKey: ["okul"] });
    },
  });

  const saatDilimiHatasi =
    gonderildi && !saatDilimiGecerliMi(saatDilimi.trim())
      ? "Saat dilimi tanımlı değil (örn. Europe/Istanbul)."
      : undefined;
  const toleransGecerli =
    !eksiBakiyeAcik ||
    (paraGecerliMi(eksiTolerans) && para(paraNormalize(eksiTolerans)).gt(0));
  const toleransHatasi =
    gonderildi && !toleransGecerli
      ? "Eksi bakiye için 0'dan büyük bir sınır girin (örn. 10,00)."
      : undefined;
  const ucretHatasi =
    gonderildi && !paraGecerliMi(kartUcreti)
      ? "Ücreti 0 veya 25,00 biçiminde girin."
      : undefined;
  const tavanHatasi =
    fotosuzAcik && gonderildi && !paraGecerliMi(fotosuzTavan)
      ? "Tavanı 50,00 biçiminde girin."
      : undefined;
  const mailHatasi =
    mailAcik && gonderildi && !/^([01]\d|2[0-3]):[0-5]\d$/.test(mailSaati)
      ? 'Saati "16:30" biçiminde girin.'
      : undefined;

  return (
    <Kart baslik={`${okul.ad} · ${okul.kod}`}>
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (
            !saatDilimiGecerliMi(saatDilimi.trim()) ||
            !toleransGecerli ||
            !paraGecerliMi(kartUcreti) ||
            (fotosuzAcik && !paraGecerliMi(fotosuzTavan)) ||
            (mailAcik && !/^([01]\d|2[0-3]):[0-5]\d$/.test(mailSaati))
          )
            return;
          kaydet.mutate();
        }}
      >
        {/* fieldset[disabled] tek yerden kilitler: yeni bir alan eklendiğinde
            kilidi ayrıca hatırlamak gerekmez. */}
        <fieldset
          disabled={!yazabilir}
          className="m-0 flex min-w-0 flex-col gap-5 border-0 p-0"
        >
          <section aria-label="Zaman ve gün sınırı">
            <h3 className="m-0 mb-2 text-lg">Zaman</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <Alan
                etiket="Saat dilimi"
                value={saatDilimi}
                onChange={(e) => setSaatDilimi(e.target.value)}
                list="saat-dilimleri"
                ipucu={`Gün sınırı bu dilime göre çözülür. Varsayılan: ${VARSAYILAN.SAAT_DILIMI}`}
                {...(saatDilimiHatasi ? { hata: saatDilimiHatasi } : {})}
              />
              <datalist id="saat-dilimleri">
                <option value="Europe/Istanbul" />
                <option value="Europe/Berlin" />
                <option value="Europe/London" />
                <option value="UTC" />
              </datalist>
            </div>
            <p className="m-0 mt-2 text-sm text-murekkep-2">
              Z raporu ve günlük limitler bu saat dilimindeki güne göre
              hesaplanır. Sunucu UTC çalışır; yanlış saat dilimi sabah raporunu
              bir önceki güne yazar.
            </p>
          </section>

          <section aria-label="Bakiye politikası">
            <h3 className="m-0 mb-2 text-lg">Bakiye ve kart</h3>
            <OnayKutusu
              etiket="Eksi bakiyeyle alışverişe izin ver"
              aciklama="Bu okulun tüm kartları için geçerlidir. Kapalıyken 0 TL bakiyeyle veya bakiyeyi aşan tutarda alışveriş yapılamaz. Mevcut borçlar korunur."
              isaretli={eksiBakiyeAcik}
              degistir={setEksiBakiyeAcik}
            />
            <p className="m-0 mt-2 text-sm text-murekkep-2">
              Değişiklik çevrimdışı kasalara yeniden bağlantı kurduklarında
              aktarılır.
            </p>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {eksiBakiyeAcik && (
                <Alan
                  etiket="Eksi bakiye toleransı"
                  value={eksiTolerans}
                  onChange={(e) => setEksiTolerans(e.target.value)}
                  inputMode="decimal"
                  ipucu="Bu okuldaki her kartın bakiyesi en fazla bu tutar kadar eksiye düşebilir."
                  {...(toleransHatasi ? { hata: toleransHatasi } : {})}
                />
              )}
              <Alan
                etiket="Kart yenileme ücreti"
                value={kartUcreti}
                onChange={(e) => setKartUcreti(e.target.value)}
                inputMode="decimal"
                ipucu="Kayıp kart yenilemede bakiyeden tahsil edilir. Bakiye yetersizse işlem başlamaz."
                {...(ucretHatasi ? { hata: ucretHatasi } : {})}
              />
            </div>
          </section>

          <section aria-label="Offline risk">
            <h3 className="m-0 mb-2 text-lg">
              Fotoğrafsız kart offline tavanı
            </h3>
            <p className="m-0 mb-2 text-sm text-murekkep-2">
              Fotoğrafı olmayan kartta görevli kimliği gözle doğrulayamaz;
              klonlama riski yüksektir. Bu ayar açıkken, fotoğrafsız kartlar
              için <strong>daha düşük</strong> bir offline harcama tavanı
              uygulanır. Varsayılan olarak kapalıdır; risk iştahı yüksek
              okullarda açılır.
            </p>
            <OnayKutusu
              etiket="Fotoğrafsız kartlar için ayrı tavan uygula"
              aciklama={`Kapalıyken terminalin genel offline tavanı geçerlidir (varsayılan ${VARSAYILAN.OFFLINE_TAVAN} ₺).`}
              isaretli={fotosuzAcik}
              degistir={setFotosuzAcik}
            />
            {fotosuzAcik && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Alan
                  etiket="Fotoğrafsız kart offline tavanı"
                  value={fotosuzTavan}
                  onChange={(e) => setFotosuzTavan(e.target.value)}
                  inputMode="decimal"
                  {...(tavanHatasi ? { hata: tavanHatasi } : {})}
                />
              </div>
            )}
          </section>

          <section aria-label="Gün sonu maili">
            <h3 className="m-0 mb-2 text-lg">Gün sonu e-postası</h3>
            <OnayKutusu
              etiket="Gün sonu özet e-postası gönderilsin"
              aciklama="Z raporu özeti, düşük stok listesi ve bekleyen yüklemeler bu saatte yöneticilere gider."
              isaretli={mailAcik}
              degistir={setMailAcik}
            />
            {mailAcik && (
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Alan
                  etiket="Gönderim saati"
                  value={mailSaati}
                  onChange={(e) => setMailSaati(e.target.value)}
                  ipucu="24 saat biçimi, örn. 16:30"
                  {...(mailHatasi ? { hata: mailHatasi } : {})}
                />
              </div>
            )}
          </section>

          <section aria-label="Kullanılabilir personel rolleri">
            <h3 className="m-0 mb-2 text-lg">
              Kullanılabilir personel rolleri
            </h3>
            <p className="m-0 mb-3 text-sm text-murekkep-2">
              Kapalı roller yeni personel kayıtlarında ve rol seçimlerinde
              gösterilmez. O role sahip mevcut personel çalışmaya devam eder.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              {TUM_ROLLER.map((rol) => {
                const zorunlu = rol === Rol.OKUL_SISTEM_YONETICISI;
                // Platform kilidi bu ayarın ÜSTÜNDEDİR (0021): açılmaya
                // çalışılırsa sunucu 400 döner, o yüzden kutu hiç açılmaz.
                const platformKapali =
                  okul.platform_kapali_roller.includes(rol);
                return (
                  <OnayKutusu
                    key={rol}
                    etiket={ROL_ADI[rol] ?? "Bilinmeyen rol"}
                    aciklama={
                      platformKapali
                        ? "Platform tarafından kapatıldı; buradan açılamaz."
                        : zorunlu
                          ? "Okulun yönetim erişimini korumak için zorunludur."
                          : undefined
                    }
                    isaretli={
                      !platformKapali && kullanilabilirRoller.includes(rol)
                    }
                    devreDisi={zorunlu || platformKapali}
                    degistir={(acik) =>
                      setKullanilabilirRoller((mevcut) =>
                        acik
                          ? [...mevcut, rol].sort((a, b) => a - b)
                          : mevcut.filter((r) => r !== rol),
                      )
                    }
                  />
                );
              })}
            </div>
          </section>
        </fieldset>

        <IslemDurumu hata={kaydet.error} />
        {kaydet.data && (
          <Uyari tur="onay" baslik="Ayarlar kaydedildi">
            Eksi tolerans:{" "}
            <Tutar deger={kaydet.data.eksi_tolerans} yonsuz boy="sm" /> · Kart
            ücreti:{" "}
            <Tutar deger={kaydet.data.kart_yenileme_ucreti} yonsuz boy="sm" />
          </Uyari>
        )}

        {yazabilir && (
          <div>
            <Buton type="submit" tur="birincil" yukleniyor={kaydet.isPending}>
              Ayarları kaydet
            </Buton>
          </div>
        )}
      </form>
    </Kart>
  );
}

// ---------------------------------------------------------------------------
// KK-1207 · Banka hesabı
// ---------------------------------------------------------------------------

function BankaHesaplari({ yazabilir }: { yazabilir: boolean }) {
  const istemci = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);

  const sorgu = useQuery({
    queryKey: ["okul-banka-hesabi"],
    queryFn: () =>
      apiOku<{ hesaplar: BankaHesabi[]; beklenti_metni: string }>(
        "/okul-banka-hesabi",
      ),
  });

  const pasifleAl = useMutation({
    mutationFn: (id: string) =>
      apiYaz("PATCH", `/okul-banka-hesabi/${id}`, { aktif: false }),
    onSuccess: () =>
      void istemci.invalidateQueries({ queryKey: ["okul-banka-hesabi"] }),
  });

  const hesaplar = sorgu.data?.hesaplar ?? [];

  return (
    <>
      <Kart
        baslik="Okul banka hesabı"
        {...(yazabilir
          ? {
              ustBilgi: (
                <Buton
                  tur="birincil"
                  onClick={() => setFormAcik((a) => !a)}
                  aria-expanded={formAcik}
                >
                  {formAcik ? "Formu kapat" : "Yeni hesap tanımla"}
                </Buton>
              ),
            }
          : {})}
      >
        <Uyari tur="uyari" baslik="IBAN değişikliği en riskli ayardır">
          Veli yüklemeleri bu hesaba gelir. Yanlış girilen bir IBAN, tüm
          velilerin parasını başka bir hesaba yönlendirir. Değişiklik{" "}
          <strong>kimin yaptığı bilgisiyle</strong> kalıcı olarak denetim
          kaydına yazılır.
        </Uyari>

        <div className="mt-3">
          <SorguDurumu
            yukleniyor={sorgu.isPending}
            hata={sorgu.error}
            bos={hesaplar.length === 0}
            bosBaslik="Banka hesabı tanımlanmamış"
            bosAciklama="Veli havale yapamaz: veli panelinde IBAN görünmez ve yükleme akışı başlamaz."
            yenile={() => void sorgu.refetch()}
          >
            <Tablo>
              <TabloBaslik>
                <th scope="col">Banka</th>
                <th scope="col">Hesap adı</th>
                <th scope="col">IBAN</th>
                <th scope="col">Açıklama şablonu</th>
                <th scope="col">Durum</th>
                <th scope="col">
                  <span className="gizli-erisilebilir">İşlem</span>
                </th>
              </TabloBaslik>
              <TabloGovde>
                {hesaplar.map((h) => (
                  <tr key={h.id}>
                    <td className="font-semibold text-murekkep">
                      {h.banka_adi}
                    </td>
                    <td className="text-sm text-murekkep-2">{h.hesap_adi}</td>
                    <td className="rakam font-mono text-xs">
                      {ibanGoster(h.iban)}
                    </td>
                    <td className="text-xs text-murekkep-2">
                      {h.aciklama_sablonu}
                    </td>
                    <td>
                      <Rozet tur={h.aktif ? "onay" : "notr"}>
                        {h.aktif ? "aktif" : "pasif"}
                      </Rozet>
                    </td>
                    <td className="text-right">
                      {yazabilir && h.aktif && (
                        <Buton
                          tur="sessiz"
                          onClick={() => pasifleAl.mutate(h.id)}
                          yukleniyor={pasifleAl.isPending}
                        >
                          Pasife al
                        </Buton>
                      )}
                    </td>
                  </tr>
                ))}
              </TabloGovde>
            </Tablo>
          </SorguDurumu>
        </div>

        <IslemDurumu hata={pasifleAl.error} />

        {sorgu.data?.beklenti_metni && (
          <div className="mt-4 rounded-md border border-cetvel bg-kagit-2 p-3">
            <p className="m-0 text-xs text-murekkep-3">
              Veliye gösterilen beklenti metni
            </p>
            <p className="m-0 mt-1 text-sm text-murekkep-2">
              {sorgu.data.beklenti_metni}
            </p>
          </div>
        )}
      </Kart>

      {yazabilir && formAcik && (
        <BankaHesabiFormu
          kapat={() => setFormAcik(false)}
          kaydedildi={() => {
            setFormAcik(false);
            void istemci.invalidateQueries({ queryKey: ["okul-banka-hesabi"] });
          }}
        />
      )}
    </>
  );
}

function BankaHesabiFormu({
  kapat,
  kaydedildi,
}: {
  kapat: () => void;
  kaydedildi: () => void;
}) {
  const [bankaAdi, setBankaAdi] = useState("");
  const [hesapAdi, setHesapAdi] = useState("");
  const [iban, setIban] = useState("");
  const [sablon, setSablon] = useState("Öğrenci referans kodu: {referans}");
  const [teyit, setTeyit] = useState(false);
  const [gonderildi, setGonderildi] = useState(false);

  const kaydet = useMutation({
    mutationFn: () =>
      apiYaz<BankaHesabi>("POST", "/okul-banka-hesabi", {
        banka_adi: bankaAdi.trim(),
        hesap_adi: hesapAdi.trim(),
        iban: ibanNormalize(iban),
        aciklama_sablonu: sablon.trim(),
        aktif: true,
      }),
    onSuccess: kaydedildi,
  });

  const ibanHatasi =
    gonderildi && !ibanGecerliMi(iban)
      ? "IBAN 'TR' ile başlamalı ve 24 rakam içermelidir."
      : undefined;
  const bankaHatasi =
    gonderildi && bankaAdi.trim().length < 2
      ? "Banka adı zorunludur."
      : undefined;
  const hesapHatasi =
    gonderildi && hesapAdi.trim().length < 2
      ? "Hesap adı zorunludur."
      : undefined;
  const sablonHatasi = sablon.includes("{referans}")
    ? undefined
    : "Şablon {referans} yer tutucusunu içermelidir; aksi halde veli havalesi öğrenciyle eşleştirilemez.";

  return (
    <Kart baslik="Yeni banka hesabı">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (
            !ibanGecerliMi(iban) ||
            bankaAdi.trim().length < 2 ||
            hesapAdi.trim().length < 2 ||
            sablonHatasi ||
            !teyit
          )
            return;
          kaydet.mutate();
        }}
      >
        <div className="grid gap-3 md:grid-cols-2">
          <Alan
            etiket="Banka adı"
            value={bankaAdi}
            onChange={(e) => setBankaAdi(e.target.value)}
            {...(bankaHatasi ? { hata: bankaHatasi } : {})}
          />
          <Alan
            etiket="Hesap adı (hesap sahibi)"
            value={hesapAdi}
            onChange={(e) => setHesapAdi(e.target.value)}
            {...(hesapHatasi ? { hata: hesapHatasi } : {})}
          />
          <Alan
            etiket="IBAN"
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            className="font-mono"
            ipucu="Bankadan kopyaladığınız boşluklu biçim kabul edilir; TR + 24 rakam."
            {...(ibanHatasi ? { hata: ibanHatasi } : {})}
          />
          <Alan
            etiket="Havale açıklama şablonu"
            value={sablon}
            onChange={(e) => setSablon(e.target.value)}
            ipucu="{referans} yer tutucusu öğrencinin yükleme referansıyla değiştirilir."
            {...(gonderildi && sablonHatasi ? { hata: sablonHatasi } : {})}
          />
        </div>

        {ibanGecerliMi(iban) && (
          <div className="rounded-md border border-cetvel bg-kagit-2 p-3">
            <p className="m-0 text-xs text-murekkep-3">
              Kaydedilecek IBAN — rakam rakam kontrol edin
            </p>
            <p className="rakam m-0 mt-1 font-mono text-lg text-murekkep">
              {ibanGoster(iban)}
            </p>
          </div>
        )}

        <OnayKutusu
          etiket="IBAN'ı banka ekranıyla karşılaştırdım, doğru"
          aciklama="Bu onay olmadan kayıt yapılamaz. Yanlış IBAN'ın geri dönüşü, paranın başka bir hesapta olmasıdır."
          isaretli={teyit}
          degistir={setTeyit}
        />

        <IslemDurumu hata={kaydet.error} />

        <div className="flex flex-wrap gap-2">
          <Buton
            type="submit"
            tur="birincil"
            disabled={!teyit}
            yukleniyor={kaydet.isPending}
          >
            Hesabı kaydet
          </Buton>
          <Buton type="button" tur="ikincil" onClick={kapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}
